import { addresses as addressesApi, delivery, NO_SCOPE, type StorefrontScope } from '../api/endpoints.ts';
import type { AddressCreated, DeliveryQuote, DeliveryTariff, DeliveryZone, MyAddress, NewAddress } from '../api/types.ts';
import { createStore, useStore } from '../core/store.ts';
import { para } from '../core/money.ts';
import type { Strings } from '../i18n/index.ts';

/**
 * Адреса клиента и условия доставки для выбранного адреса.
 *
 * Адрес определяет склад, а склад — и наличие товаров, и тариф доставки. Витрина открывается
 * без адреса, от склада по умолчанию; адрес спрашивается на первом «в корзину», чтобы
 * «сюда не доставляем» прозвучало до полной корзины, а не на оформлении. Выбор переживает
 * перезапуск.
 */

const SELECTED_KEY = 'trotty10.client.address';
const GUEST_KEY = 'trotty10.client.guest-address';

export const GUEST_ADDRESS_ID = 'guest';

/** Адрес, выбранный до входа: лежит в браузере и становится сохранённым при первом входе. */
export type GuestAddress = {
  text: string;
  lat: number;
  lng: number;
  comment: string | null;
  entrance: string | null;
  floor: string | null;
  apartment: string | null;
  intercom: string | null;
};

export type AddressBookState = {
  addresses: MyAddress[];
  selectedId: string | null;
  guest: GuestAddress | null;
  quote: DeliveryQuote | null;
  isLoading: boolean;
  /** Адреса вошедшего хотя бы раз загружены (удачно или нет): с этого момента известна область витрины. */
  loaded: boolean;
};

function readGuest(): GuestAddress | null {
  try {
    const raw = localStorage.getItem(GUEST_KEY);
    return raw ? (JSON.parse(raw) as GuestAddress) : null;
  } catch {
    return null;
  }
}

export const addressBookStore = createStore<AddressBookState>({
  addresses: [],
  selectedId: localStorage.getItem(SELECTED_KEY),
  guest: readGuest(),
  quote: null,
  isLoading: false,
  loaded: false,
});

const patch = (next: Partial<AddressBookState>) => addressBookStore.set((state) => ({ ...state, ...next }));

function guestAsAddress(guest: GuestAddress): MyAddress {
  return { id: GUEST_ADDRESS_ID, ...guest, deliverable: true };
}

function savedSelected(state: AddressBookState): MyAddress | null {
  return state.addresses.find((address) => address.id === state.selectedId) ?? null;
}

/** Сохранённый адрес в работе или точка гостя, пока аккаунта нет. */
export function selectedAddress(state: AddressBookState): MyAddress | null {
  return savedSelected(state) ?? (state.guest ? guestAsAddress(state.guest) : null);
}

/**
 * Выбранный адрес вне зон доставки: сохранённый, который сервер пометил недоставляемым
 * (зоны поменялись после сохранения), или точка гостя, которой тариф отказал.
 */
export function selectedOutOfZone(state: AddressBookState): boolean {
  const saved = savedSelected(state);
  if (saved) return !saved.deliverable;
  if (state.guest) return state.quote?.covered === false;
  return false;
}

let lastScope: StorefrontScope = NO_SCOPE;

/**
 * Область витрины: что несёт каждый запрос каталога. У адреса вне зон склада нет, витрина
 * ответила бы 422, поэтому полки идут от склада по умолчанию, а экраны просят другой адрес.
 * Тот же по смыслу ответ возвращается тем же объектом.
 */
export function storefrontScope(state: AddressBookState): StorefrontScope {
  let next: StorefrontScope = NO_SCOPE;
  const saved = savedSelected(state);
  if (saved) {
    if (saved.deliverable) next = { kind: 'address', addressId: saved.id };
  } else if (state.guest && state.quote?.covered !== false) {
    next = { kind: 'point', lat: state.guest.lat, lng: state.guest.lng };
  }
  if (JSON.stringify(next) !== JSON.stringify(lastScope)) lastScope = next;
  return lastScope;
}

export function currentScope(): StorefrontScope {
  return storefrontScope(addressBookStore.get());
}

/** Тариф выбранного адреса; до загрузки условий его нет. */
export function tariffOf(state: AddressBookState): DeliveryTariff | null {
  return state.quote?.tariff ?? null;
}

/**
 * Стоимость доставки по сумме товаров: выше порога бесплатно, нулевой порог его выключает.
 * Повторяет правило сервера, потому что цену надо показать до оформления.
 */
export function deliveryFee(tariff: DeliveryTariff | null, itemsTotal: bigint): bigint {
  if (!tariff) return 0n;
  const freeFrom = para(tariff.freeFromRsd);
  return freeFrom > 0n && itemsTotal >= freeFrom ? 0n : para(tariff.feeRsd);
}

/** Сколько не хватает до порога; нулевой порог выключает подсказку. */
export function missingTo(threshold: string | null | undefined, sum: bigint): bigint | null {
  const value = para(threshold);
  return value > 0n && sum < value ? value - sum : null;
}

/** Обещание по выбранному адресу, «≈ 18 min»; без условий показывается диапазон сети. */
export function etaText(state: AddressBookState, strings: Strings): string | null {
  const minutes = state.quote?.etaMinutes;
  return minutes === null || minutes === undefined ? null : strings.etaAbout(minutes);
}

/** «Ulaz 2 · Sprat 3 · Stan 12 · Interfon 12» для списков; null без подробностей. */
export function addressDetailsLine(address: MyAddress, strings: Strings): string | null {
  const parts = [
    address.entrance ? `${strings.addressEntrance} ${address.entrance}` : null,
    address.floor ? `${strings.addressFloor} ${address.floor}` : null,
    address.apartment ? `${strings.addressApartment} ${address.apartment}` : null,
    address.intercom ? `${strings.addressIntercom} ${address.intercom}` : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Улица и дом без города: текст адреса до первой запятой. */
export function streetLine(address: MyAddress): string {
  return address.text.split(',')[0]?.trim() || address.text;
}

export function useAddressBook(): AddressBookState {
  return useStore(addressBookStore);
}

export function selectAddress(id: string | null): void {
  patch({ selectedId: id });
  if (id) localStorage.setItem(SELECTED_KEY, id);
  else localStorage.removeItem(SELECTED_KEY);
}

/** Точка гостя: хранится в браузере до входа. */
export function setGuest(guest: GuestAddress | null): void {
  patch({ guest });
  if (guest) localStorage.setItem(GUEST_KEY, JSON.stringify(guest));
  else localStorage.removeItem(GUEST_KEY);
}

export async function refreshQuote(): Promise<void> {
  const state = addressBookStore.get();
  // Отказ тарифа точке гостя сам обнуляет область; спрашивать надо по точке, а не по области.
  const saved = savedSelected(state);
  const target: StorefrontScope = saved
    ? saved.deliverable
      ? { kind: 'address', addressId: saved.id }
      : NO_SCOPE
    : state.guest
      ? { kind: 'point', lat: state.guest.lat, lng: state.guest.lng }
      : NO_SCOPE;

  if (target.kind === 'none') {
    patch({ quote: null });
    return;
  }
  patch({ quote: await delivery.quote(target) });
}

export async function selectAndRefresh(id: string): Promise<void> {
  selectAddress(id);
  await refreshQuote();
}

export async function setGuestAndRefresh(guest: GuestAddress): Promise<void> {
  setGuest(guest);
  await refreshQuote();
}

/**
 * Адреса вошедшего. Адрес гостя, выбранный до входа, сначала сохраняется в аккаунт, чтобы
 * витрина и оформление держались одной точки.
 */
export async function loadAddresses(): Promise<void> {
  patch({ isLoading: true });
  try {
    const guest = addressBookStore.get().guest;
    if (guest) {
      // Снимается с устройства до запроса: вторая загрузка рядом не должна сохранить ту же
      // точку дважды. Неудачный запрос возвращает её на место.
      setGuest(null);
      try {
        const created = await addressesApi.create(newAddressBody(guest));
        selectAddress(created.id);
      } catch (error) {
        setGuest(guest);
        throw error;
      }
    }

    const items = (await addressesApi.list()).items;
    patch({ addresses: items });

    // Выбранный адрес могли удалить на другом устройстве, или он выпал из зон: берётся первый
    // доставляемый. Без доставляемых выбирается первый какой есть: экраны назовут его и
    // попросят новый.
    const current = savedSelected(addressBookStore.get());
    if (current?.deliverable !== true) {
      const fallback = items.find((address) => address.deliverable);
      if (fallback) selectAddress(fallback.id);
      else if (!current) selectAddress(items[0]?.id ?? null);
    }
    await refreshQuote();
  } finally {
    patch({ isLoading: false, loaded: true });
  }
}

/** Выход: адреса принадлежат человеку. Следующий начинает гостем. */
export function forgetAccountAddresses(): void {
  selectAddress(null);
  patch({ addresses: [], quote: null, loaded: false });
}

function clean(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function newAddressBody(input: {
  text: string;
  lat: number;
  lng: number;
  comment?: string | null;
  entrance?: string | null;
  floor?: string | null;
  apartment?: string | null;
  intercom?: string | null;
}): NewAddress {
  return {
    text: input.text,
    lat: input.lat,
    lng: input.lng,
    comment: clean(input.comment),
    entrance: clean(input.entrance),
    floor: clean(input.floor),
    apartment: clean(input.apartment),
    intercom: clean(input.intercom),
  };
}

export async function addAddress(input: Parameters<typeof newAddressBody>[0]): Promise<AddressCreated> {
  const created = await addressesApi.create(newAddressBody(input));
  if (!created.outOfZone) selectAddress(created.id);
  await loadAddresses();
  return created;
}

export async function removeAddress(id: string): Promise<void> {
  await addressesApi.remove(id);
  if (addressBookStore.get().selectedId === id) selectAddress(null);
  await loadAddresses();
}

/** Попросить сообщить, когда доставка начнётся по адресу вне зон: придёт уведомление `zone_open`. */
export async function requestCoverage(addressId: string): Promise<void> {
  await addressesApi.requestCoverage(addressId);
}

/** Условия для точки на карте: до сохранения адреса надо знать, доставляем ли сюда. */
export function quoteForPoint(lat: number, lng: number, text?: string): Promise<DeliveryQuote> {
  return delivery.quoteByPoint(lat, lng, text);
}

/** Контуры обслуживаемых зон для карты адреса. */
export async function deliveryZones(): Promise<DeliveryZone[]> {
  return (await delivery.zones()).items;
}
